/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  File as FileIcon,
  Upload,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
} from '@saleshandy/icons';
import Select from '../../../../shared/design-system/components/select';
import { RootState } from '../../../../store/root-reducer';
import { setColumnMapping, setUploadedFile } from '../../csv-enrichment-slice';
import { EnrichmentType } from '../../enums/csv-enrichment.enums';
import { ColumnMapping } from '../../types/csv-enrichment.types';
import {
  parseCSVHeaders,
  autoDetectColumnMapping,
} from '../../helpers/csv-enrichment.helpers';
import toaster, { Theme } from '../../../../shared/toaster';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import Alert from '../../../../shared/design-system/ui/alert';

type HeaderOption = {
  key: string;
  label: string;
};

const PEOPLE_MAPPING_FIELDS: {
  key: keyof ColumnMapping;
  label: string;
  required: boolean;
}[] = [
  { key: 'firstName', label: 'First Name', required: true },
  { key: 'lastName', label: 'Last Name', required: true },
  { key: 'companyName', label: 'Company Name', required: true },
  { key: 'linkedinUrl', label: 'LinkedIn URL', required: true },
  { key: 'companyDomain', label: 'Company Domain/Website', required: false },
];

const COMPANY_MAPPING_FIELDS: {
  key: keyof ColumnMapping;
  label: string;
  required: boolean;
}[] = [
  { key: 'companyName', label: 'Company Name', required: true },
  { key: 'linkedinUrl', label: 'Company LinkedIn URL', required: true },
  { key: 'companyDomain', label: 'Company Domain/Website', required: true },
];

const EMPTY_OPTION: HeaderOption = { key: '', label: 'Select column...' };

type MappingRowProps = {
  field: { key: keyof ColumnMapping; label: string; required: boolean };
  selectedHeader: string | null;
  availableOptions: HeaderOption[];
  previewValue: string;
  onMappingChange: (field: keyof ColumnMapping, value: string) => void;
};

const MappingRow: React.FC<MappingRowProps> = ({
  field,
  selectedHeader,
  availableOptions,
  previewValue,
  onMappingChange,
}) => {
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = searchText
    ? [
        EMPTY_OPTION,
        ...availableOptions
          .slice(1) // skip leading EMPTY_OPTION to avoid duplicate
          .filter((opt) =>
            opt.label.toLowerCase().includes(searchText.toLowerCase()),
          ),
      ]
    : availableOptions;

  return (
    <div className="bs-csv-enrichment__mapping-row">
      <div className="bs-csv-enrichment__mapping-label">
        <span className="label">{field.label}</span>
        {!field.required && (
          <span className="regular-1 gray-txt-11"> (optional)</span>
        )}
      </div>
      <div className="bs-csv-enrichment__mapping-select-wrapper">
        <Select<HeaderOption>
          options={filteredOptions}
          selectedOptionKey={selectedHeader || ''}
          selectedOptionRenderer={([opt]) => (
            <TruncatedTextWithAutoTooltip
              className="d-block"
              text={opt?.label || 'Select column...'}
            />
          )}
          optionRenderer={(opt) => (
            <TruncatedTextWithAutoTooltip
              className="d-block"
              text={opt?.label}
            />
          )}
          onChange={([opt]) => {
            onMappingChange(field.key, opt.key as string);
            setSearchText('');
          }}
          onToggle={(isOpen) => {
            if (isOpen) {
              requestAnimationFrame(() => {
                searchInputRef.current?.focus();
              });
            } else {
              setSearchText('');
            }
          }}
          placeholder="Select column..."
          className="map-field-select"
          customHeader={
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className="bs-csv-enrichment__search-header"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <input
                ref={searchInputRef}
                type="text"
                className="bs-csv-enrichment__search-input"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          }
        />
      </div>
      <div className="bs-csv-enrichment__mapping-preview regular-1 gray-txt-11">
        {previewValue ? (
          <TruncatedTextWithAutoTooltip
            className="d-block"
            text={`${previewValue}`}
          />
        ) : (
          '\u2014'
        )}
      </div>
    </div>
  );
};

const UploadMapStep: React.FC = () => {
  const dispatch = useDispatch();
  const { uploadedFile, columnMapping, selectedType } = useSelector(
    (state: RootState) => state.csvEnrichment,
  );
  const maxCsvEnrichmentRecords = useSelector(
    (state: RootState) => (state.home as any)?.leadFinder?.csvEnrichment,
  ) as { row?: number; column?: number; size?: number } | undefined;

  // Backend provides max file size in bytes.
  const maxBytes = maxCsvEnrichmentRecords?.size ?? 25 * 1024 * 1024; // 25 MB default
  const maxSizeMbLabel = Math.round(maxBytes / (1024 * 1024));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(true);
  const [duplicateHeaderError, setDuplicateHeaderError] = useState<string>('');

  const isCsvFile = (file: File) => {
    const name = (file?.name || '').toLowerCase();
    return name.endsWith('.csv');
  };

  const getExactDuplicateHeaders = (headersList: string[]): string[] => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    headersList.forEach((h) => {
      const key = String(h ?? '').trim();
      if (!key) return;
      if (seen.has(key)) duplicates.add(key);
      else seen.add(key);
    });
    return Array.from(duplicates);
  };

  const handleFileSelected = async (file: File) => {
    setDuplicateHeaderError('');
    if (!isCsvFile(file)) {
      toaster.error('Only CSV files are supported', { theme: Theme.New });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > maxBytes) {
      toaster.error(`Cannot upload file greater than ${maxSizeMbLabel} MB`, {
        theme: Theme.New,
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const fileInfo = await parseCSVHeaders(file);
      const duplicates = getExactDuplicateHeaders(fileInfo.headers || []);
      if (duplicates.length > 0) {
        const msg =
          'The uploaded file has duplicate column headers. Please ensure the column header is unique and re-upload the file.';
        // If a file is already uploaded, this selection is coming from "Reupload CSV"
        if (uploadedFile) {
          toaster.error(msg, { theme: Theme.New });
          setDuplicateHeaderError('');
        } else {
          setDuplicateHeaderError(msg);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      dispatch(setUploadedFile(fileInfo));
      const autoMapping = autoDetectColumnMapping(fileInfo.headers);
      dispatch(setColumnMapping({ ...columnMapping, ...autoMapping }));
    } catch {
      // stay on step 2
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
  };

  // const handleFileRemove = () => {
  //   dispatch(setUploadedFile(null));
  //   dispatch(
  //     setColumnMapping({
  //       firstName: null,
  //       lastName: null,
  //       companyName: null,
  //       companyDomain: null,
  //       linkedinUrl: null,
  //     }),
  //   );
  //   if (fileInputRef.current) fileInputRef.current.value = '';
  // };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && isCsvFile(file)) {
      handleFileSelected(file);
    }
  };

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    dispatch(
      setColumnMapping({
        ...columnMapping,
        [field]: value || null,
      }),
    );
  };

  const headers = uploadedFile?.headers || [];
  const sampleRow = uploadedFile?.sampleRow || {};

  // Build header options once
  const allHeaderOptions: HeaderOption[] = useMemo(
    () =>
      headers
        ?.filter((h) => h?.trim() !== '')
        ?.map((h) => ({ key: h, label: h })),
    [headers],
  );

  const mappingFields =
    selectedType === EnrichmentType.PEOPLE
      ? PEOPLE_MAPPING_FIELDS
      : COMPANY_MAPPING_FIELDS;

  const getUsedHeaders = (currentField: keyof ColumnMapping): string[] =>
    mappingFields
      .filter((f) => f.key !== currentField && columnMapping[f.key])
      .map((f) => columnMapping[f.key] as string);

  const rowLimit = maxCsvEnrichmentRecords?.row;
  const columnLimit = maxCsvEnrichmentRecords?.column;
  const isRowCountExceeded = uploadedFile?.rowCount > rowLimit;
  const isColumnCountExceeded = uploadedFile?.headers?.length > columnLimit;
  const hasLimitError = isRowCountExceeded || isColumnCountExceeded;

  const isAllMapped =
    selectedType === EnrichmentType.COMPANY
      ? !!columnMapping.companyName &&
        !!(columnMapping.companyDomain || columnMapping.linkedinUrl)
      : !!columnMapping.linkedinUrl ||
        (!!columnMapping.firstName &&
          !!columnMapping.lastName &&
          !!(
            columnMapping.companyName ||
            columnMapping.companyDomain ||
            columnMapping.linkedinUrl
          ));

  return (
    <div className="bs-csv-enrichment__upload-map">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Section 1: File upload area */}
      {!uploadedFile ? (
        <>
          {duplicateHeaderError && (
            <Alert
              variant="warning"
              icon={AlertTriangle}
              description={
                <span className="regular-1">{duplicateHeaderError}</span>
              }
              className="required-field-warning"
            />
          )}
          <div
            className={`bs-csv-enrichment__dropzone${
              isDragging ? ' bs-csv-enrichment__dropzone--active' : ''
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === 'Enter' && fileInputRef.current?.click()
            }
          >
            <Upload width={40} height={40} className="gray-txt-11" />
            <div className="bs-csv-enrichment__dropzone-text">
              <span className="drag-and-drop blue-txt-15">
                Drag & drop your CSV file here
              </span>
              <span className="regular-2 gray-txt-15">or click to browse</span>
              <span className="regular-1 gray-txt-11">
                CSV files only, max {maxSizeMbLabel} MB & {rowLimit} records
              </span>
            </div>
          </div>
          <span className="bs-csv-enrichment__dropzone-hint">
            {selectedType === EnrichmentType.PEOPLE ? (
              <>
                Your CSV must include either a <strong>LinkedIn URL</strong>, or{' '}
                <strong>First Name</strong>, <strong>Last Name</strong>, along
                with <strong>Company Name</strong> or{' '}
                <strong>Company Domain/Website</strong>. The more fields you
                provide, the more accurate your results.
              </>
            ) : (
              <>
                Your CSV must include either <strong>Company Name</strong> and{' '}
                <strong>Company Domain/Website</strong> or{' '}
                <strong>Company LinkedIn URL</strong>. Adding both improves
                accuracy.
              </>
            )}
          </span>
        </>
      ) : (
        <div className="bs-csv-enrichment__bordered-section">
          <div className="bs-csv-enrichment__file-preview">
            <div className="bs-csv-enrichment__file-preview-info">
              <FileIcon width={20} height={20} className="gray-txt-11" />
              <div className="bs-csv-enrichment__file-preview-name-wrap">
                <div className="bs-csv-enrichment__file-preview-name-truncate">
                  <TruncatedTextWithAutoTooltip
                    className="uploaded-file-name"
                    text={uploadedFile.name}
                  />
                </div>
                <span className="uploaded-file-count">
                  &middot; {uploadedFile.rowCount.toLocaleString()} rows
                  &middot; {uploadedFile.headers.length} cols
                </span>
              </div>
            </div>
            {/* <div
              className="bs-csv-enrichment__file-remove-btn"
              {...accessibleOnClick(handleFileRemove)}
            >
              <Cross width={16} height={16} />
            </div> */}

            <button
              type="button"
              className="bs-csv-enrichment__change-file-link"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload width={14} height={14} />
              Reupload CSV
            </button>
          </div>
        </div>
      )}

      {uploadedFile && hasLimitError && (
        <Alert
          variant="warning"
          icon={AlertTriangle}
          description={
            <span className="regular-1">
              {isRowCountExceeded && isColumnCountExceeded
                ? `Uploaded File exceeds both row and column limits. Please reduce the number of rows and columns.`
                : isRowCountExceeded
                ? `Uploaded File exceeds row limit of ${rowLimit.toLocaleString()} records. Please reduce the number of rows.`
                : `Uploaded File exceeds column limit of ${columnLimit?.toLocaleString()} records. Please reduce the number of columns.`}
            </span>
          }
          className="required-field-warning"
        />
      )}
      {uploadedFile && !hasLimitError && !isAllMapped && (
        <Alert
          variant="warning"
          icon={AlertTriangle}
          description={
            <span className="regular-1">
              {selectedType === EnrichmentType.PEOPLE
                ? 'Required fields missing. Map either a LinkedIn URL, or First Name, Last Name with Company Name or Company Domain/Website to proceed.'
                : 'Required fields missing. Map Company Name along with either Company Domain/Website or LinkedIn URL to proceed.'}
            </span>
          }
          className="required-field-warning"
        />
      )}

      {/* Section 2: Field Mapping accordion */}
      {uploadedFile && (
        <div className="bs-csv-enrichment__bordered-section">
          {/* Accordion header */}
          <div
            className={`bs-csv-enrichment__accordion-header${
              !isFieldMappingOpen
                ? ' bs-csv-enrichment__accordion-header--collapsed'
                : ''
            }`}
            onClick={() => setIsFieldMappingOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === 'Enter' && setIsFieldMappingOpen((prev) => !prev)
            }
          >
            <span className="title">Field Mapping</span>
            {isFieldMappingOpen ? (
              <ChevronUp width={14} height={14} color="#9ca3af" />
            ) : (
              <ChevronDown width={14} height={14} color="#9ca3af" />
            )}
          </div>

          {/* Accordion body */}
          {isFieldMappingOpen && (
            <div className="bs-csv-enrichment__accordion-body">
              <div className="regular-1 gray-txt-11">
                For the best results, map as many CSV fields as you can.
              </div>

              <div className="bs-csv-enrichment__mapping-section">
                <div className="bs-csv-enrichment__mapping-header">
                  <span className="semibold-1 gray-txt-15">We need</span>
                  <span className="semibold-1 gray-txt-15">In your file</span>
                  <span className="semibold-1 gray-txt-15">Preview:</span>
                </div>

                {mappingFields?.map((field) => {
                  const usedHeaders = getUsedHeaders(field.key);
                  const selectedHeader = columnMapping[field.key];
                  const previewValue = selectedHeader
                    ? sampleRow[selectedHeader]
                    : '';

                  const availableOptions = [
                    EMPTY_OPTION,
                    ...allHeaderOptions.filter(
                      (opt) => !usedHeaders.includes(opt.key),
                    ),
                  ];

                  return (
                    <MappingRow
                      key={field.key}
                      field={field}
                      selectedHeader={selectedHeader}
                      availableOptions={availableOptions}
                      previewValue={previewValue ? `${previewValue}` : ''}
                      onMappingChange={handleMappingChange}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadMapStep;
